import { useMemo } from 'react';
import {
  useGetTransportsQueryWithPolling,
  useSelectedRequestId,
  type EtlTransportResponse,
  type EtlVehicleDetails,
  type EtlProviderDetails,
  type StatusDisplayItem,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import type { StatusPageState } from '../../types';
import { formatTransportStatuses, getStatusPageState } from '../etlStatusUtils';

interface AirAmbulanceEtlStatusResult {
  transport: EtlTransportResponse | undefined;
  selectedRequestId: string | null;
  isLoading: boolean;
  formattedStatuses: StatusDisplayItem[];
  /** The most recent status. The remaining statuses are in {@link remainingStatuses}) */
  latestStatus: StatusDisplayItem | null;
  /** Remaining statuses (all except {@link latestStatus}) */
  remainingStatuses: StatusDisplayItem[];
  latestVehicle: EtlVehicleDetails | null;
  latestProvider: EtlProviderDetails | null;
  /** The URL that the user can open in their browser to track the status. */
  trackingUrl: string | null;
  /** Brand tail (aircraft identifier) if available */
  brandTail: string | null;
  /** Latest ETA display string from status updates */
  latestEta: string | null;
  statusPageState: StatusPageState;
}

/**
 * Hook to fetch and monitor Air Ambulance transport status from ETL service.
 * Uses RTK Query with polling for automatic data freshness.
 */
function useAirAmbulanceEtlStatus(): AirAmbulanceEtlStatusResult {
  // AFAIK we only need have a component to show the status of the currently
  // selected transport request. If we need to show status for a non-selected
  // request, we can modify this hook to take a requestId as a parameter.
  const selectedRequestId = useSelectedRequestId();

  const { data, isFetching } = useGetTransportsQueryWithPolling();
  const transports = data?.transports;

  const transport = transports
    ? transports.find(t => t.gmr_transport_id === selectedRequestId)
    : undefined;

  const formattedStatuses = useMemo(
    () => (transport ? formatTransportStatuses(transport.statuses) : []),
    [transport],
  );

  const latestStatus =
    formattedStatuses.length > 0 ? formattedStatuses[0] : null;
  const remainingStatuses = formattedStatuses.slice(1);

  const latestVehicle =
    formattedStatuses.find(s => s.vehicle?.name || s.vehicle?.id)?.vehicle ??
    null;
  const latestProvider =
    formattedStatuses.find(s => s.provider?.name)?.provider ?? null;
  const trackingUrl =
    formattedStatuses.find(s => s.trackingUrl)?.trackingUrl ?? null;
  const brandTail = latestVehicle?.name ?? null;
  const latestEta =
    formattedStatuses.find(s => s.eta?.length)?.eta?.[0] ?? null;
  const statusPageState = getStatusPageState(transport, formattedStatuses);

  return {
    transport,
    selectedRequestId,
    isLoading: isFetching,
    formattedStatuses,
    latestStatus,
    remainingStatuses,
    latestVehicle,
    latestProvider,
    trackingUrl,
    brandTail,
    latestEta,
    statusPageState,
  };
}

export default useAirAmbulanceEtlStatus;
